import type {
  Assessment,
  CheckAnswer,
  GradingCompany,
  ListingResearch,
  RawCondition,
  ResearchEventType,
  ResearchRecord,
  ResearchStatus,
  ResearchStatusFilter,
} from "@pokifolio/contracts";

export const RAW_CONDITION_LABELS: Readonly<Record<RawCondition, string>> = {
  near_mint: "Near mint",
  lightly_played: "Lightly played",
  moderately_played: "Moderately played",
  heavily_played: "Heavily played",
  damaged: "Damaged",
};

export const CHECK_ANSWER_LABELS: Readonly<Record<CheckAnswer, string>> = {
  yes: "Yes",
  no: "No",
  cant_see: "Can't see",
};

export const GRADING_COMPANY_LABELS: Readonly<Record<GradingCompany, string>> = {
  psa: "PSA",
  bgs: "BGS (Beckett)",
  cgc: "CGC",
  sgc: "SGC",
  other: "Other",
};

/** One line for lists: "Near mint", "PSA 10", "BGS (Beckett) 9.5". */
export function describeAssessment(assessment: Assessment): string {
  if (assessment.cardType === "graded" && assessment.gradingCompany !== null) {
    return `${GRADING_COMPANY_LABELS[assessment.gradingCompany]} ${String(assessment.grade ?? "")}`.trim();
  }
  return assessment.overall === null ? "Raw" : RAW_CONDITION_LABELS[assessment.overall];
}

export const STATUS_FILTER_LABELS: Readonly<Record<ResearchStatusFilter, string>> = {
  open: "Open (not closed)",
  draft: "Draft",
  waiting_approval: "Waiting for approval",
  approved: "Approved",
  rejected: "Rejected",
  closed: "Closed",
  all: "All, including closed",
};

export const EVENT_LABELS: Readonly<Record<ResearchEventType, string>> = {
  saved: "Saved to Research",
  assessed: "Condition checked",
  price_confirmed: "Price confirmed",
  submitted: "Sent for approval",
  withdrawn: "Request withdrawn",
  approved: "Approved",
  rejected: "Rejected",
  reopened: "Reopened",
  closed: "Closed",
  deleted: "Deleted",
};

/** "22 Oct 2026" in the viewer's language. */
export function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString([], { day: "numeric", month: "short", year: "numeric" });
}

export const RESEARCH_STATUS_LABELS: Readonly<Record<ResearchStatus, string>> = {
  draft: "Draft",
  waiting_approval: "Waiting for approval",
  approved: "Approved",
  rejected: "Rejected",
  closed: "Closed",
};

export const RESEARCH_STATUS_TONES: Readonly<
  Record<ResearchStatus, "brand" | "neutral" | "success" | "warning" | "danger">
> = {
  draft: "brand",
  waiting_approval: "warning",
  approved: "success",
  rejected: "danger",
  closed: "neutral",
};

/** The short form shown in search results, from a record just saved. */
export function toListingResearch(record: ResearchRecord): ListingResearch {
  return {
    listingId: record.listing.listingId,
    researchId: record.id,
    status: record.status,
    owner: record.owner,
    card: { id: record.card.id, name: record.card.name, number: record.card.number },
  };
}
